// src/pages/relatorios/RelatorioPedidos.jsx
import React, { useState, useEffect } from 'react';
import { 
  FaFilter, FaFileExcel, FaSearch, FaCalendarAlt,
  FaShoppingCart, FaUsers, FaMoneyBillWave, FaPercentage,
  FaSpinner, FaInfoCircle
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import supabase from '../../supabaseClient';
import * as XLSX from 'xlsx';

const RelatorioPedidos = () => {
  const { hasRole, user, userProfile } = useAuth();
  
  // Estados
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expositorLogado, setExpositorLogado] = useState(null);
  const [expositores, setExpositores] = useState([]);
  const [produtos, setProdutos] = useState([]);
  
  // Estados para filtros - EXPANDIDOS
  const [filters, setFilters] = useState({
    dataInicio: '',
    dataFim: '',
    expositor: '',
    nomeFantasia: '',
    numeroPedido: '',
    produto: '',
    status: ''
  });
  
  // Estados para estatísticas
  const [stats, setStats] = useState({
    totalPedidos: 0,
    valorTotal: 0,
    descontoTotal: 0,
    quantidadeTotal: 0
  });

  // Buscar expositor logado baseado no email
  const fetchExpositorLogado = async () => {
    if (!user?.email || hasRole('admin')) {
      return null; // Admin vê todos os pedidos
    }

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('*')
        .eq('email', user.email)
        .eq('ativo', true)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar expositor logado:', error);
      return null;
    }
  };

  // Carregar expositores para o filtro (apenas para admin)
  const fetchExpositores = async () => {
    if (!hasRole('admin')) return;

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa, cnpj, email')
        .eq('ativo', true)
        .order('nome_empresa');
      
      if (error) throw error;
      setExpositores(data || []);
    } catch (error) {
      console.error('Erro ao buscar expositores:', error);
    }
  };

  // Carregar produtos para o filtro
  const fetchProdutos = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_produtos')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');
      
      if (error) throw error;
      setProdutos(data || []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  };

  // Carregar dados dos pedidos com filtros aplicados
  const fetchPedidos = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Buscando pedidos com filtros:', filters);
      
      // Primeiro buscar expositor se for expositor logado
      const expositor = await fetchExpositorLogado();
      setExpositorLogado(expositor);
      
      // Query base para buscar pedidos com itens
      let query = supabase
        .from('caex_pedidos')
        .select(`
          id,
          created_at,
          valor_total,
          status,
          observacoes,
          expositor_id,
          evento_id,
          caex_expositores (
            id,
            nome_empresa,
            cnpj,
            email
          ),
          caex_pedidos_itens (
            id,
            produto_id,
            quantidade,
            preco_unitario,
            preco_total,
            preco_original,
            desconto,
            tipo_desconto,
            caex_produtos (
              id,
              nome,
              descricao
            )
          )
        `)
        .order('created_at', { ascending: false });

      // Aplicar filtro por expositor se não for admin
      if (!hasRole('admin') && expositor) {
        query = query.eq('expositor_id', expositor.id);
      }

      // NOVOS FILTROS APLICADOS

      // Filtro por data início
      if (filters.dataInicio) {
        query = query.gte('created_at', `${filters.dataInicio}T00:00:00`);
      }

      // Filtro por data fim
      if (filters.dataFim) {
        query = query.lte('created_at', `${filters.dataFim}T23:59:59`);
      }
      
      // Filtro por status
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      // Filtro por ID do expositor (para admin)
      if (filters.expositor && hasRole('admin')) {
        query = query.eq('expositor_id', filters.expositor);
      }

      // Filtro por número do pedido
      if (filters.numeroPedido) {
        // Converte para número se possível, senão busca como string
        const numeroPedidoInt = parseInt(filters.numeroPedido);
        if (!isNaN(numeroPedidoInt)) {
          query = query.eq('id', numeroPedidoInt);
        }
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('Erro ao buscar pedidos:', fetchError);
        throw fetchError;
      }

      console.log('Pedidos carregados:', data);

      // Verificar se o expositor foi encontrado (apenas para não-admin)
      if (!hasRole('admin') && !expositor) {
        setError('Expositor não encontrado para este usuário. Entre em contato com o administrador.');
        setLoading(false);
        return;
      }

      // Processar dados para o relatório
      let pedidosProcessados = [];
      let totalValor = 0;
      let totalDesconto = 0;
      let totalQuantidade = 0;

      data.forEach((pedido, pedidoIndex) => {
        if (pedido.caex_pedidos_itens && pedido.caex_pedidos_itens.length > 0) {
          pedido.caex_pedidos_itens.forEach((item, itemIndex) => {
            const numeroPedido = pedido.id;
            const expositorNome = pedido.caex_expositores?.nome_empresa || 'Expositor não encontrado';
            const produtoNome = item.caex_produtos?.nome || 'Produto não encontrado';
            const quantidade = item.quantidade || 0;
            const valorUnitario = parseFloat(item.preco_unitario) || 0;
            const subtotal = parseFloat(item.preco_total) || 0;
            const valorOriginal = parseFloat(item.preco_original) || valorUnitario;
            const desconto = parseFloat(item.desconto) || 0;
            
            // Calcular valor do desconto
            let valorDesconto = 0;
            if (desconto > 0) {
              if (item.tipo_desconto === 'percentage') {
                valorDesconto = (valorOriginal * desconto / 100) * quantidade;
              } else {
                valorDesconto = desconto * quantidade;
              }
            }

            pedidosProcessados.push({
              numeroPedido,
              dataPedido: new Date(pedido.created_at).toLocaleDateString('pt-BR'),
              nomeExpositor: expositorNome,
              produto: produtoNome,
              quantidade,
              valor: valorOriginal, // ALTERADO: agora usa preco_original
              subtotal,
              desconto: valorDesconto,
              valorTotal: subtotal,
              status: pedido.status,
              pedidoId: pedido.id,
              itemId: item.id
            });

            // Acumular estatísticas
            totalValor += subtotal;
            totalDesconto += valorDesconto;
            totalQuantidade += quantidade;
          });
        } else {
          // Pedido sem itens
          const numeroPedido = pedido.id;
          const expositorNome = pedido.caex_expositores?.nome_empresa || 'Expositor não encontrado';
          
          pedidosProcessados.push({
            numeroPedido,
            dataPedido: new Date(pedido.created_at).toLocaleDateString('pt-BR'),
            nomeExpositor: expositorNome,
            produto: 'Sem itens',
            quantidade: 0,
            valor: 0,
            subtotal: 0,
            desconto: 0,
            valorTotal: parseFloat(pedido.valor_total) || 0,
            status: pedido.status,
            pedidoId: pedido.id,
            itemId: null
          });

          totalValor += parseFloat(pedido.valor_total) || 0;
        }
      });

      // APLICAR FILTROS LOCAIS (que não podem ser aplicados na query SQL)

      // Filtro por nome fantasia (busca parcial no nome do expositor)
      if (filters.nomeFantasia) {
        pedidosProcessados = pedidosProcessados.filter(pedido =>
          pedido.nomeExpositor.toLowerCase().includes(filters.nomeFantasia.toLowerCase())
        );
      }

      // Filtro por produto (busca parcial no nome do produto)
      if (filters.produto) {
        pedidosProcessados = pedidosProcessados.filter(pedido =>
          pedido.produto.toLowerCase().includes(filters.produto.toLowerCase())
        );
      }

      // Recalcular estatísticas após filtros locais
      if (filters.nomeFantasia || filters.produto) {
        totalValor = pedidosProcessados.reduce((sum, p) => sum + p.valorTotal, 0);
        totalDesconto = pedidosProcessados.reduce((sum, p) => sum + p.desconto, 0);
        totalQuantidade = pedidosProcessados.reduce((sum, p) => sum + p.quantidade, 0);
      }

      setPedidos(pedidosProcessados);
      setStats({
        totalPedidos: data.length,
        valorTotal: totalValor,
        descontoTotal: totalDesconto,
        quantidadeTotal: totalQuantidade
      });

    } catch (err) {
      console.error('Erro ao carregar pedidos:', err);
      setError(err.message || 'Erro ao carregar dados dos pedidos');
    } finally {
      setLoading(false);
    }
  };

  // Função para exportar para Excel
  const exportToExcel = () => {
    try {
      if (pedidos.length === 0) {
        alert('Não há dados para exportar.');
        return;
      }

      // Preparar dados para o Excel
      const dadosExcel = pedidos.map(pedido => ({
        'Nº do Pedido': pedido.numeroPedido,
        'Data do Pedido': pedido.dataPedido,
        'Nome do Expositor': pedido.nomeExpositor,
        'Produto': pedido.produto,
        'Quantidade': pedido.quantidade,
        'Valor': `R$ ${pedido.valor.toFixed(2).replace('.', ',')}`,
        'Subtotal': `R$ ${pedido.subtotal.toFixed(2).replace('.', ',')}`,
        'Desconto': `R$ ${pedido.desconto.toFixed(2).replace('.', ',')}`,
        'Valor Total': `R$ ${pedido.valorTotal.toFixed(2).replace('.', ',')}`
      }));

      // Adicionar linha de totais
      dadosExcel.push({
        'Nº do Pedido': '',
        'Data do Pedido': '',
        'Nome do Expositor': '',
        'Produto': 'TOTAIS:',
        'Quantidade': stats.quantidadeTotal,
        'Valor': '',
        'Subtotal': '',
        'Desconto': `R$ ${stats.descontoTotal.toFixed(2).replace('.', ',')}`,
        'Valor Total': `R$ ${stats.valorTotal.toFixed(2).replace('.', ',')}`
      });

      // Criar workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dadosExcel);

      // Configurar larguras das colunas
      const colWidths = [
        { wch: 15 }, // Nº do Pedido
        { wch: 15 }, // Data do Pedido
        { wch: 30 }, // Nome do Expositor
        { wch: 30 }, // Produto
        { wch: 12 }, // Quantidade
        { wch: 15 }, // Valor
        { wch: 15 }, // Subtotal
        { wch: 15 }, // Desconto
        { wch: 15 }  // Valor Total
      ];
      worksheet['!cols'] = colWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatório de Pedidos');

      // Gerar nome do arquivo com data/hora atual
      const now = new Date();
      const dataHora = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const nomeArquivo = `relatorio_pedidos_${dataHora}.xlsx`;

      // Salvar arquivo
      XLSX.writeFile(workbook, nomeArquivo);

      console.log('Excel exportado com sucesso:', nomeArquivo);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert('Erro ao exportar para Excel. Tente novamente.');
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    if (user?.email) {
      fetchExpositores();
      fetchProdutos();
      fetchPedidos();
    }
  }, [user]);

  // Aplicar filtros
  const handleApplyFilters = () => {
    fetchPedidos();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      dataInicio: '',
      dataFim: '',
      expositor: '',
      nomeFantasia: '',
      numeroPedido: '',
      produto: '',
      status: ''
    });
    setTimeout(() => {
      fetchPedidos();
    }, 100);
  };

  // Handler para mudanças nos filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Renderizar estatísticas
  const renderStats = () => (
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-icon">
          <FaShoppingCart />
        </div>
        <div className="stat-content">
          <h3>{stats.totalPedidos}</h3>
          <p>Total de Pedidos</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaUsers />
        </div>
        <div className="stat-content">
          <h3>{stats.quantidadeTotal}</h3>
          <p>Itens Vendidos</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaMoneyBillWave />
        </div>
        <div className="stat-content">
          <h3>{formatCurrency(stats.valorTotal)}</h3>
          <p>Valor Total</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaPercentage />
        </div>
        <div className="stat-content">
          <h3>{formatCurrency(stats.descontoTotal)}</h3>
          <p>Descontos Aplicados</p>
        </div>
      </div>
    </div>
  );

  // Renderizar filtros expandidos
  const renderFilters = () => (
    <div className="filters-section">
      <div className="filters-grid">
        {/* Primeira linha - Datas */}
        <div className="filter-group">
          <label>Data Início</label>
          <input
            type="date"
            value={filters.dataInicio}
            onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Data Fim</label>
          <input
            type="date"
            value={filters.dataFim}
            onChange={(e) => handleFilterChange('dataFim', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-input"
          >
            <option value="">Todos</option>
            <option value="pendente">Pendente</option>
            <option value="aprovado">Aprovado</option>
            <option value="rejeitado">Rejeitado</option>
          </select>
        </div>

        {/* Segunda linha - Expositor (apenas para admin) */}
        {hasRole('admin') && (
          <div className="filter-group">
            <label>Expositor</label>
            <select
              value={filters.expositor}
              onChange={(e) => handleFilterChange('expositor', e.target.value)}
              className="filter-input"
            >
              <option value="">Todos os Expositores</option>
              {expositores.map(expositor => (
                <option key={expositor.id} value={expositor.id}>
                  {expositor.nome_empresa}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label>Nome Fantasia Expositor</label>
          <input
            type="text"
            placeholder="Digite o nome da empresa..."
            value={filters.nomeFantasia}
            onChange={(e) => handleFilterChange('nomeFantasia', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Número do Pedido</label>
          <input
            type="text"
            placeholder="Digite o número do pedido..."
            value={filters.numeroPedido}
            onChange={(e) => handleFilterChange('numeroPedido', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Produto</label>
          <input
            type="text"
            placeholder="Digite o nome do produto..."
            value={filters.produto}
            onChange={(e) => handleFilterChange('produto', e.target.value)}
            className="filter-input"
          />
        </div>
      </div>
      
      <div className="filters-actions">
        <button 
          className="btn-filter apply"
          onClick={handleApplyFilters}
          disabled={loading}
        >
          <FaSearch />
          Aplicar Filtros
        </button>
        
        <button 
          className="btn-filter clear"
          onClick={handleClearFilters}
          disabled={loading}
        >
          Limpar Filtros
        </button>
      </div>
    </div>
  );

  // Renderizar tabela
  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <FaSpinner className="loading-spinner" />
          <p>Carregando dados dos pedidos...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <FaInfoCircle className="error-icon" />
          <h3>Erro ao carregar dados</h3>
          <p>{error}</p>
          {error.includes('Expositor não encontrado') ? (
            <div className="error-details">
              <p><strong>Email do usuário:</strong> {user?.email}</p>
              <p>Verifique se existe um expositor cadastrado com este email e se está ativo no sistema.</p>
            </div>
          ) : (
            <button className="btn-retry" onClick={fetchPedidos}>
              Tentar Novamente
            </button>
          )}
        </div>
      );
    }

    if (pedidos.length === 0) {
      return (
        <div className="empty-container">
          <FaShoppingCart className="empty-icon" />
          <h3>Nenhum pedido encontrado</h3>
          <p>Não há pedidos que correspondam aos filtros aplicados.</p>
        </div>
      );
    }

    return (
      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Nº do Pedido</th>
              <th>Data do Pedido</th>
              <th>Nome do Expositor</th>
              <th>Produto</th>
              <th>Quantidade</th>
              <th>Valor</th>
              <th>Subtotal</th>
              <th>Desconto</th>
              <th>Valor Total</th>
            </tr>
          </thead>
          <tbody>
            {pedidos.map((pedido, index) => (
              <tr key={`${pedido.pedidoId}-${pedido.itemId || index}`}>
                <td>{pedido.numeroPedido}</td>
                <td>{pedido.dataPedido}</td>
                <td>{pedido.nomeExpositor}</td>
                <td>{pedido.produto}</td>
                <td>{pedido.quantidade}</td>
                <td>{formatCurrency(pedido.valor)}</td>
                <td>{formatCurrency(pedido.subtotal)}</td>
                <td>{formatCurrency(pedido.desconto)}</td>
                <td className="valor-total">{formatCurrency(pedido.valorTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="relatorio-container">
      {/* Header com estatísticas */}
      <div className="relatorio-header">
        {/* Informação do expositor se não for admin */}
        {!hasRole('admin') && expositorLogado && (
          <div className="expositor-info">
            <h4>Relatório de: {expositorLogado.nome_empresa}</h4>
            <p>CNPJ: {expositorLogado.cnpj}</p>
          </div>
        )}
        
        <div className="header-controls">
          <button 
            className={`btn-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
          
          <div className="export-buttons">
            <button 
              className="btn-export excel" 
              onClick={exportToExcel}
              disabled={loading || pedidos.length === 0}
            >
              <FaFileExcel />
              Excel
            </button>
          </div>
        </div>

        {renderStats()}
      </div>

      {/* Filtros */}
      {showFilters && renderFilters()}

      {/* Tabela de dados */}
      <div className="relatorio-content">
        {renderTable()}
      </div>
    </div>
  );
};

export default RelatorioPedidos;